'use client'

import * as React from 'react'
import { BookOpen } from 'lucide-react'

import type { BookDetails } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@ui/web/components'
import { MyCollectionIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { CardAction } from './cards/card-action'
import { PutBooksInMyCollectionForm } from '../forms'
import { LoveBookButton } from '../lib/love-book-button'
import { ShareContentButton } from '../lib/share-content-dialog'
import { BookDetailBody } from './book-detail-body'

interface BookDetailModalProps {
  book?: BookDetails
  isLoading?: boolean
  isError?: boolean
  open: boolean
  onClose: () => void
  renderBookAction?: (book: BookDetails) => CardAction
  renderCollectionLink?: RenderLink
  renderLoveBookLink?: RenderLink
  onTagClick?: (type: 'author' | 'publisher' | 'level' | 'theme', id: string) => void
  contentClassName?: string
  overlayClassName?: string
  /**
   * Show the share icon next to the love button. Off by default because the trigger
   * pulls in the tRPC and auth contexts, which provider-free renders (Storybook) lack.
   * The button hides itself for child accounts.
   */
  withShareCta?: boolean
}

export function BookDetailModal({
  book,
  isLoading,
  isError,
  open,
  onClose,
  renderBookAction,
  renderCollectionLink,
  renderLoveBookLink,
  onTagClick,
  contentClassName,
  overlayClassName,
  withShareCta = false,
}: BookDetailModalProps) {
  const [collectionDialogOpen, setCollectionDialogOpen] = React.useState(false)

  const action = book && renderBookAction ? renderBookAction(book) : null

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) onClose()
        }}
      >
        <DialogContent
          className={cn(
            'srd:w-full srd:max-w-170 srd:p-0 srd:overflow-hidden srd:rounded-2xl srd:gap-4 srd:max-h-[90dvh] srd:overflow-y-auto',
            contentClassName,
          )}
          overlayClassName={overlayClassName}
        >
          <DialogHeader className='srd:sr-only'>
            <DialogTitle>{book?.title ?? 'Detail Buku'}</DialogTitle>
            <DialogDescription>{book?.synopsis ?? ''}</DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className='srd:flex srd:flex-col srd:md:flex-row srd:gap-0'>
              <div className='srd:flex srd:items-center srd:justify-center srd:p-6 srd:md:w-55 srd:md:shrink-0'>
                <Skeleton className='srd:w-45 srd:md:w-full srd:aspect-3/4 srd:rounded-xl' />
              </div>
              <div className='srd:flex srd:flex-col srd:gap-y-3 srd:p-6 srd:flex-1'>
                <Skeleton className='srd:h-6 srd:w-3/4' />
                <Skeleton className='srd:h-4 srd:w-1/3' />
                <Skeleton className='srd:h-16 srd:w-full' />
                <div className='srd:flex srd:gap-x-2 srd:flex-wrap srd:gap-y-1.5'>
                  <Skeleton className='srd:h-6 srd:w-16 srd:rounded-full' />
                  <Skeleton className='srd:h-6 srd:w-20 srd:rounded-full' />
                  <Skeleton className='srd:h-6 srd:w-14 srd:rounded-full' />
                </div>
              </div>
            </div>
          ) : isError ? (
            <div className='srd:flex srd:items-center srd:justify-center srd:p-10'>
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-error srd:text-center')}>
                Gagal memuat detail buku. Coba lagi nanti.
              </p>
            </div>
          ) : book ? (
            <BookDetailBody book={book} onTagClick={onTagClick} />
          ) : null}

          <DialogFooter className='srd:mx-0 srd:mb-0 srd:flex-row srd:items-center srd:justify-between srd:py-3 srd:gap-x-3'>
            <div className='srd:flex srd:items-center srd:gap-x-1'>
              {book && renderCollectionLink && (
                <Button
                  variant='ghost'
                  size='icon'
                  className='srd:shrink-0'
                  onClick={() => setCollectionDialogOpen(true)}
                  title='Masukkan ke koleksi'
                >
                  <MyCollectionIcon className='srd:size-6 srd:text-neutral-black' />
                </Button>
              )}
              {book && renderLoveBookLink && (
                <LoveBookButton data={book} type='icon' renderLinkLovedBooks={renderLoveBookLink} />
              )}
              {book && withShareCta && (
                <ShareContentButton
                  entityType='BOOK'
                  entityId={book.id}
                  cover={{ kind: 'book', coverUrl: book.coverUrl }}
                />
              )}
            </div>

            <div className='srd:flex srd:items-center srd:gap-x-2'>
              <Button variant='outline' onClick={onClose} className='srd:rounded-full'>
                Tutup
              </Button>

              {book &&
                action &&
                (action.type === 'link' ? (
                  <Button className='srd:rounded-full' asChild>
                    {action.renderWrapper(
                      <>
                        <BookOpen className='srd:size-5 srd:shrink-0 srd:mr-2' />
                        Baca
                      </>,
                    )}
                  </Button>
                ) : (
                  <Button className='srd:rounded-full' onClick={action.onClick}>
                    <BookOpen className='srd:size-5 srd:shrink-0 srd:mr-2' />
                    Baca
                  </Button>
                ))}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {book && renderCollectionLink && (
        <Dialog
          open={collectionDialogOpen}
          onOpenChange={(o) => {
            setCollectionDialogOpen(o)
          }}
        >
          <DialogContent className='srd:w-full srd:max-w-125'>
            <DialogHeader>
              <DialogTitle>Tambahkan ke Kompilasiku</DialogTitle>
              <DialogDescription>Tambahkan buku ini ke kompilasiku.</DialogDescription>
            </DialogHeader>
            {collectionDialogOpen && (
              <PutBooksInMyCollectionForm
                bookId={book.id}
                onSuccess={() => setCollectionDialogOpen(false)}
                onCancel={() => setCollectionDialogOpen(false)}
                renderCollectionLink={renderCollectionLink}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
